import { Slider } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import throttle from "lodash/throttle";
import React from "react";
import { appMetadataNoise } from "src/pods/noise";
import errorMessages, { ErrorType } from "src/pods/noise/data/errorMessages";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import logEvent from "src/utils/logEvent";
import bargraph from "src/pods/noise/assets/bargraph.png";

type NoiseRemoteProps = {
  classId: string;
  mountPoint: string;
  displayOpen: (path: string, payload?: { type: string; payload: { gain: 0; showSensitivityInput: boolean } }) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  sendNoise: (payload: { gain: number }) => void;
  isCasting: boolean;
  isConnected: boolean;
  microphonePermissionErrorName: ErrorType;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class NoiseRemote extends React.Component<NoiseRemoteProps> {
  constructor(props: NoiseRemoteProps) {
    super(props);
    this.changeSensitivity = throttle(this.changeSensitivity, 500);
  }

  componentDidMount() {
    this.props.displayOpen("/apps/display/noise", {
      type: "RECEIVE_NOISE",
      payload: { gain: 0, showSensitivityInput: this.props.mountPoint === "teach" },
    });
    logEvent({ eventName: "noise.setup.open" });
  }

  componentWillUnmount() {
    logEvent({ eventName: "noise.setup.close" });
  }

  render() {
    const { isCasting, isConnected, microphonePermissionErrorName, classId, mountPoint } = this.props;

    let errorMessage;
    if (!isCasting) {
      if (isConnected) {
        errorMessage = translate("dojo.toolkit:noise_meter.errors.connected");
      } else {
        errorMessage = translate("dojo.toolkit:noise_meter.errors.disconnected");
      }
    } else if (microphonePermissionErrorName) {
      errorMessage = errorMessages(microphonePermissionErrorName);
    }

    if (mountPoint === "teach") return <div />;

    return (
      <AppPage {...appMetadataNoise()} classId={classId} mountPoint={mountPoint}>
        <AppModal
          {...appMetadataNoise()}
          onRequestHide={() => {
            this.props.displayClose();
            this.props.remoteClose();
          }}
          onRequestStart={() => this.props.displayOpen("/apps/display/noise")}
          onRequestStop={() => this.props.displayClose()}
        >
          <section sx={displayStyle}>
            <img src={bargraph} sx={bargraphStyle} />
            {isCasting && !microphonePermissionErrorName && (
              <Slider
                aria-label={translate({
                  str: "dojo.toolkit:noise_meter.noise_range",
                  fallback: "Noise sensitivity range",
                })}
                label={translate({
                  str: "dojo.toolkit:noise_meter.sensitivity",
                  fallback: "Sensitivity",
                })}
                defaultValue={[0]}
                min={-125}
                max={125}
                step={1}
                onChange={(value) => this.changeSensitivity(value[0])}
                sx={{ marginTop: "l" }}
              />
            )}
            {errorMessage && <p sx={errorMessageStyle}>{errorMessage}</p>}
          </section>
        </AppModal>
      </AppPage>
    );
  }

  changeSensitivity(gain: number) {
    this.props.sendNoise({ gain });
  }
}

const displayStyle: ThemeUIStyleObject = {
  margin: "dt_l",
  textAlign: "center",
};

const bargraphStyle = {
  marginBottom: RAW_cssValue("42px"),
};

const errorMessageStyle = {
  fontWeight: "600",
};
